// Browser mirror of `quoteSendConfirmationReviewSchema` in packages/shared/src/schemas/quote-send.ts.
// Pinned by quoteSendReviewContract.parity.test.ts; see docs/frontend-package-boundaries.md.
import { z } from 'zod';

const id = z.string().uuid();
const instant = z.string().datetime();
const revision = z.string().regex(/^sha256:[a-f0-9]{64}$/);

/** Mirror of `QUOTE_DOCUMENT_LANGUAGES` (#3633); the parity test pins it to the shared schema. */
export const QUOTE_DOCUMENT_LANGUAGES = ['fi', 'sv', 'en'] as const;

export const quoteSendConfirmationReviewSchema = z
  .object({
    version: z.literal(1),
    quote: z
      .object({
        id,
        workOrderNumber: z.string().min(1),
        registrationNumber: z.string().min(1),
        updatedAt: instant,
        quoteRevision: revision,
        quoteExpiresAt: instant.nullable(),
        workshopTimezone: z.string().min(1),
        netSubtotalCents: z.number().int(),
        taxCents: z.number().int(),
        grossTotalCents: z.number().int(),
        defaultVatPercent: z.number(),
      })
      .strict(),
    channel: z.enum(['email', 'whatsapp']),
    recipient: z.string().min(1),
    recipientSource: z.enum(['on_file', 'entered']),
    message: z.string().nullable(),
    // Required: a card from before the language was frozen is unreadable, never read as Finnish.
    language: z.enum(QUOTE_DOCUMENT_LANGUAGES),
    delivers: z.literal('quote_pdf_and_response_link'),
  })
  .strict();
export type QuoteSendConfirmationReview = z.infer<typeof quoteSendConfirmationReviewSchema>;

const SEND_PRESENTERS = new Set([
  'confirmation.presenters.sendQuoteEmail',
  'confirmation.presenters.sendQuoteWhatsapp',
]);
const CHANNEL_BY_PRESENTER: Record<string, 'email' | 'whatsapp'> = {
  'confirmation.presenters.sendQuoteEmail': 'email',
  'confirmation.presenters.sendQuoteWhatsapp': 'whatsapp',
};

/**
 * A send card without a readable review (or one whose channel disagrees with
 * its headline) must not be confirmable: the headline alone does not show the
 * message, the version or the totals the customer will receive.
 */
export function parseQuoteSendReview(
  params: Record<string, unknown>,
  presenterKey?: string
):
  | { kind: 'absent' }
  | { kind: 'invalid' }
  | { kind: 'review'; review: QuoteSendConfirmationReview } {
  if (!('quoteSendReview' in params)) {
    return SEND_PRESENTERS.has(presenterKey ?? '') ? { kind: 'invalid' } : { kind: 'absent' };
  }
  const parsed = quoteSendConfirmationReviewSchema.safeParse(params.quoteSendReview);
  if (!parsed.success) return { kind: 'invalid' };
  const expected = presenterKey ? CHANNEL_BY_PRESENTER[presenterKey] : undefined;
  if (expected === undefined || expected !== parsed.data.channel) return { kind: 'invalid' };
  try {
    new Intl.DateTimeFormat('en', { timeZone: parsed.data.quote.workshopTimezone });
  } catch {
    return { kind: 'invalid' };
  }
  return { kind: 'review', review: parsed.data };
}
